import React, {useEffect, useState} from 'react';
import {
    Box,
    Button, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import {Check, HourglassTop, QuestionMark} from '@mui/icons-material';


import {deleteAgentFile, getAgentById, getAgentFiles, uploadAgentFile} from '../../services/DataService';
import {Agent} from "../../models/Agent";
import {StorageFile, StorageFileIndexStatus} from "../../models/StorageFile";
import {setSnackbarOpen} from "../../store/slices/snackbar.slice";
import {useDispatch, useSelector} from "react-redux";
import {useAuth} from "../../context/useAuth";
import {RootState} from "../../store/store";
import DeleteIcon from '@mui/icons-material/Delete';
import {MaiSplitPage} from "../../components/splitPage/MaiSplitPage";
import theme from "../../theme/theme";
import {FileUpload} from "../../components/fileUpload/FileUpload";

export const AgentFilesPage = () => {
    const {selectedAgent} = useSelector((state: RootState) => state.agents);
    const [agentInfo, setAgentInfo] = useState<Agent | null>(null);
    const dispatch = useDispatch();
    const {token} = useAuth();
    const [files, setFiles] = useState<StorageFile[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<string | null>(null);
    const [loadingFiles, setLoadingFiles] = useState(false);

    useEffect(() => {
        if (selectedAgent?.gcpAgentId && token) {
            setLoadingFiles(true);
            getAgentById(selectedAgent?.gcpAgentId, token)
                .then(res => setAgentInfo(res.data))
                .catch(err => console.error(err));

            getAgentFiles(selectedAgent?.gcpAgentId, token)
                .then(res => {
                    setLoadingFiles(false);
                    setFiles(res.data)
                }) // evtl. transformieren je nach Backend-Format
                .catch(err => console.error(err));
        }
    }, [selectedAgent?.gcpAgentId]);

    const handleFileUpload = (file: File) => {
        if (!selectedAgent?.gcpAgentId || !token) return;

        uploadAgentFile(selectedAgent.gcpAgentId, file, token)
            .then(() => {
                dispatch(setSnackbarOpen({ snackbarMessage: "Upload erfolgreich!", snackbarSeverity: 'success' }));
                return getAgentFiles(selectedAgent.gcpAgentId, token);
            })
            .then(res => setFiles(res.data))
            .catch(err => console.error(err));
    };

    const confirmDelete = (fileId: string) => {
        setFileToDelete(fileId);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setFileToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (selectedAgent && token && fileToDelete) {
            deleteAgentFile(selectedAgent.gcpAgentId, fileToDelete, token)
                .then(() => {
                    dispatch(setSnackbarOpen({snackbarMessage: "Löschen erfolgreich!", snackbarSeverity: 'success'}));
                    return getAgentFiles(selectedAgent.gcpAgentId, token);
                })
                .then(res => setFiles(res.data))
                .catch(err => console.error(err))
                .finally(() => {
                    handleDialogClose();
                });
        }
    };

    const page = (
        <Box>
            {loadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box py={2}>
                    <Typography variant="h6" px={2}>Bereits hochgeladene Dateien</Typography>
                    {files.length === 0 ? (
                        <Typography variant="explanation" px={2}>Keine Dateien vorhanden.</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Größe</strong></TableCell>
                                        <TableCell><strong>Typ</strong></TableCell>
                                        <TableCell><strong>Datum</strong></TableCell>
                                        <TableCell align="center"><strong>Indexierungsstatus</strong></TableCell>
                                        <TableCell align="center"><strong>Aktionen</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {files.map((file) => (
                                        <TableRow key={file.id}>
                                            <TableCell>{file.name}</TableCell>
                                            <TableCell>{file.sizeReadable}</TableCell>
                                            <TableCell>{file.fileExtension}</TableCell>
                                            <TableCell>{file.updateTime}</TableCell>
                                            <TableCell align="center">
                                                {file.indexStatus === StorageFileIndexStatus.IN_PROGRESS && (
                                                    <Tooltip title="Indexierung läuft...">
                                                        <HourglassTop color="disabled"/>
                                                    </Tooltip>
                                                )}
                                                {file.indexStatus === StorageFileIndexStatus.COMPLETED && (
                                                    <Tooltip title="Daten wurden erfolgreich indexiert.">
                                                        <Check color="success"/>
                                                    </Tooltip>
                                                )}
                                                {file.indexStatus === StorageFileIndexStatus.UNKNOWN && (
                                                    <Tooltip title="Unbekannter Status">
                                                        <QuestionMark color="error"/>
                                                    </Tooltip>
                                                )}

                                            </TableCell>
                                            <TableCell align={"center"}>
                                                <Tooltip title="Datei löschen">
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => confirmDelete(file.id)}
                                                        sx={{ color: theme.palette.secondary.main }}
                                                    >
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                </Tooltip>

                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            <Typography variant="h6" px={2}>Upload</Typography>
            <FileUpload onUpload={handleFileUpload} />
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
            >
                <DialogTitle>Datei löschen</DialogTitle>
                <DialogContent>
                    <Typography>Möchtest du diese Datei wirklich löschen?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Abbrechen
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    return (
        <MaiSplitPage
            singlePage={true}
            childrenLeft={page}
        />
    );
};